// src/components/Header.jsx
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useBranding } from "../hooks/useBranding";
import { useAuth } from "../hooks/useAuth";
import { useData } from "../hooks/useData";
import { useMobile } from "../hooks/useMobile";
import { withCloudinaryLogoOptimization } from "../utils/cloudinary";
import { NilamLogo, Btn, B, fonts } from "./UI";

export default function Header() {
  const { branding }       = useBranding();
  const { admin, logout }  = useAuth();
  const { categories }     = useData();
  const navigate           = useNavigate();
  const location           = useLocation();
  const isMobile           = useMobile();
  const [menuOpen,   setMenuOpen]   = useState(false);
  const [scrolled,   setScrolled]   = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => setMenuOpen(false), [location.pathname]);

  const accent = branding.primaryColor || B.orange;

  return (
    <>
      <header style={{
        background:   "rgba(255,255,255,0.92)",
        borderBottom: `1px solid ${B.borderLight}`,
        position:     "sticky", top: 0, zIndex: 300,
        boxShadow:    scrolled ? "0 12px 40px rgba(224,107,79,0.16)" : "0 4px 18px rgba(46,41,38,0.06)",
        transition:   "box-shadow 0.3s",
      }}>
        {/* Top bar */}
        <div style={{ maxWidth: 1280, margin: "0 auto", padding: "0 24px", display: "flex", alignItems: "center", justifyContent: "space-between", height: 68 }}>

          {/* Logo */}
          <div onClick={() => navigate("/")} style={{ display: "flex", alignItems: "center", gap: 12, cursor: "pointer", flexShrink: 0 }}>
            <NilamLogo size={60} logoUrl={withCloudinaryLogoOptimization(branding.logoUrl)} />
            <div>
              <div style={{ fontWeight: 900, fontSize: 20, color: accent, lineHeight: 1, fontFamily: fonts.display, letterSpacing: "0.01em" }}>
                {branding.brandName}
              </div>
              <div style={{ fontSize: 9, color: B.textLight, letterSpacing: "0.22em", textTransform: "uppercase", marginTop: 2, fontFamily: fonts.body }}>
                {branding.tagline}
              </div>
            </div>
          </div>

          {!isMobile && (
            <>
              {/* Desktop category nav */}
            <nav style={{ display: "flex", alignItems: "center", gap: 4, flex: 1, justifyContent: "center", overflow: "hidden" }}>
                {categories.slice(0, 6).map(cat => {
                  const active = location.pathname === `/category/${cat.slug}`;
                  return (
                    <button key={cat.id} onClick={() => navigate(`/category/${cat.slug}`)}
                      style={{
                        background: active ? accent + "1A" : "transparent",
                        border: active ? `1px solid ${accent}44` : "1px solid transparent",
                        borderRadius: 6, padding: "6px 12px", cursor: "pointer",
                        color: active ? accent : B.textMid,
                        fontSize: 12, fontWeight: active ? 700 : 500,
                        fontFamily: fonts.body, transition: "all 0.2s",
                        whiteSpace: "nowrap",
                      }}
                      onMouseEnter={e => { e.target.style.color = accent; }}
                      onMouseLeave={e => { e.target.style.color = active ? accent : B.textMid; }}
                    >
                      {cat.name}
                    </button>
                  );
                })}
              </nav>
            </>
          )}
          

          {/* Right actions */}
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0 }}>
            {!admin && !isMobile && location.pathname === "/" && (
              <Btn onClick={() => navigate("/admin-login")} variant="ghostDark" small>Admin Login</Btn>
            )}

            {admin && !isMobile && (
              <>
                {location.pathname !== "/admin" && (
                  <Btn onClick={() => navigate("/admin")} variant="ghostDark" small>Admin Dashboard</Btn>
                )}
                {location.pathname == "/admin" && (
                  <Btn onClick={() => navigate("/")} variant="ghostDark" small>Frontend</Btn>
                )}
                <Btn onClick={() => { logout(); navigate("/"); }} variant="ghostDark" small>Logout</Btn>
              </>
            )}

            {/* Hamburger — mobile only */}
            {isMobile && (
              <button onClick={() => setMenuOpen(o => !o)} className="mobile-menu-btn">
                {menuOpen ? "✕" : "☰"}
              </button>
            )}
          </div>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div style={{ background: "#fffaf6", borderTop: `1px solid ${B.borderLight}`, padding: "12px 0 16px" }}>
            {categories.map(cat => (
              <button key={cat.id} onClick={() => navigate(`/category/${cat.slug}`)}
                style={{ display: "block", width: "100%", background: "none", border: "none", textAlign: "left", padding: "11px 24px", color: B.text, fontSize: 14, cursor: "pointer", fontFamily: fonts.body }}>
                {cat.icon} {cat.name}
              </button>
            ))}
            <div style={{ padding: "8px 24px 0", borderTop: `1px solid ${B.borderLight}`, marginTop: 8 }}>
              <button onClick={() => navigate("/about")}    style={{ display: "block", background: "none", border: "none", color: B.textLight, fontSize: 12, cursor: "pointer", padding: "6px 0", fontFamily: fonts.body }}>About Us</button>
              <button onClick={() => navigate("/how-to-order")} style={{ display: "block", background: "none", border: "none", color: B.textLight, fontSize: 12, cursor: "pointer", padding: "6px 0", fontFamily: fonts.body }}>How To Order</button>
            </div>
            <div style={{ padding: "8px 24px 0", borderTop: `1px solid ${B.borderLight}`, marginTop: 8, display: "flex", justifyContent: "center" }}>
              {!admin && isMobile && location.pathname === "/" && (
              <Btn onClick={() => navigate("/admin-login")} variant="ghostDark" small >Admin Login</Btn>
              )}
              {admin && (
                <>
                  {location.pathname !== "/admin" && (
                    <Btn onClick={() => navigate("/admin")} variant="ghostDark" large>Admin Dashboard</Btn>
                  )}
                  {location.pathname == "/admin" && (
                  <Btn onClick={() => navigate("/")} variant="ghostDark" small>Frontend</Btn>
                  )}
                  <Btn onClick={() => { logout(); navigate("/"); }} variant="ghostDark" large>Logout</Btn>
                </>
              )}
            </div>
          </div>
        )}
      </header>
    </>
  );
}
